import React, { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import * as Sentry from '@sentry/react'
import { DarkModeProvider, useDarkMode } from './contexts/DarkModeContext'
import Header from './components/common/Header'
import Hero from './components/home/Hero'
import Features from './components/home/Features'
import ResumeImprovement from './components/home/ResumeImprovement'
import Testimonials from './components/home/Testimonials'
import WhyLoveUs from './components/home/WhyLoveUs'
import Pricing from './components/home/Pricing'
import InterviewPreview from './components/home/InterviewPreview'
import CTABanner from './components/home/CTABanner'
import Footer from './components/common/Footer'
import StickyCTA from './components/common/StickyCTA'
import PrivacyPolicy from './components/home/PrivacyPolicy'
import EULA from './components/home/EULA'
import Dashboard from './components/dashboard/Dashboard'
import Profile from './components/profile/Profile'
import Settings from './components/common/Settings'
import Sharing from './components/sharing/Sharing'
import Signup from './components/signup/Signup'
import ScrollToTop from './components/common/ScrollToTop'
import CookieConsent from './components/common/CookieConsent'
import EnvironmentBanner from './components/common/EnvironmentBanner'
import { getEnvironment } from './utils/environment'
import { trackPageView } from './utils/analytics'
import { checkAndStoreReferralCode } from './utils/referral'
import './App.scss'

function Home() {
  return (
    <>
      <Hero />
      <Features />
      <ResumeImprovement />
      <InterviewPreview />
      <Testimonials />
      <WhyLoveUs />
      <Pricing />
      <CTABanner />
      <StickyCTA />
    </>
  )
}

interface ProtectedRouteProps {
  children: React.ReactNode
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth0()

  // Test mode: bypass Auth0 if test flag is set in localStorage
  // This allows Playwright tests to work without fully mocking Auth0
  // Check synchronously during render (not in useEffect) to avoid delay
  const isTestMode = typeof window !== 'undefined' && 
    (localStorage.getItem('__TEST_MODE__') === 'true' || 
     window.location.search.includes('testMode=true'));

  if (isTestMode) {
    return <>{children}</>;
  }

  // Show loading state while Auth0 checks the session from localStorage
  if (isLoading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Checking authentication...</p>
        </div>
      </div>
    )
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/" replace />
}

function AppContent() {
  const location = useLocation()
  const { isDarkMode } = useDarkMode()

  // Check for referral code in URL on mount
  useEffect(() => {
    checkAndStoreReferralCode()
  }, [])

  // Update document title with environment indicator
  useEffect(() => {
    const updateTitle = () => {
      const environment = getEnvironment()
      const currentTitle = document.title || 'JobsReadyAI'
      
      // Remove any existing environment prefix (in case title was already set)
      const titleWithoutPrefix = currentTitle.replace(/^\[(DEV|STAGING)\]\s*/, '')
      
      // Add environment prefix if needed
      if (environment === 'dev') {
        document.title = `[DEV] ${titleWithoutPrefix}`
      } else if (environment === 'staging') {
        document.title = `[STAGING] ${titleWithoutPrefix}`
      } else {
        // Production - use title as is (without prefix)
        document.title = titleWithoutPrefix
      }
    }
    
    // Update immediately
    updateTitle()
    
    // Also update after a brief delay to catch any title changes from child components
    const timeoutId = setTimeout(updateTitle, 100)
    
    return () => clearTimeout(timeoutId)
  }, [location]) // Update when route changes (pages might set their own titles)

  // Track page views when route changes
  useEffect(() => {
    trackPageView(location.pathname + location.search, document.title)
  }, [location])

  return (
    <>
      <EnvironmentBanner />
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/eula" element={<EULA />} />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/sharing" 
          element={
            <ProtectedRoute>
              <Sharing />
            </ProtectedRoute>
          } 
        />
      </Routes>
      <Footer />
      <CookieConsent />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? 'dark' : 'light'}
      />
    </>
  )
}

function App() {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE

  // Staging now uses a separate domain/bucket, so no basename needed
  // React Router will use default (no basename) for all environments
  const basename = undefined
  const redirectUri = window.location.origin

  // Fallback values for development (replace with your Auth0 values)
  const auth0Config = {
    domain: domain || 'your-auth0-domain.auth0.com',
    clientId: clientId || 'your-client-id',
    authorizationParams: {
      redirect_uri: redirectUri,
      audience: audience || undefined,
      scope: 'openid profile email',
    },
    cacheLocation: 'localstorage' as const // Persist session in localStorage so it survives page refresh
  }

  return (
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>} showDialog>
      <Auth0Provider {...auth0Config}>
        <DarkModeProvider>
          <Router basename={basename}>
            <ScrollToTop />
            <div className="app">
              <AppContent />
            </div>
          </Router>
        </DarkModeProvider>
      </Auth0Provider>
    </Sentry.ErrorBoundary>
  )
}

export default App
